import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { MagnifyingGlass, ArrowRight } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';

const API = process.env.REACT_APP_BACKEND_URL;

export function ImportBabyMenuDialog({ open, onOpenChange, patientId, onImported }) {
  const [sources, setSources] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/baby-menus/available-for-import?search=${search}`);
        setSources(data.filter(s => s.patient_id !== patientId));
      } catch {} finally { setLoading(false); }
    })();
  }, [open, search, patientId]);

  const handleImport = async () => {
    if (!selectedWeek) return;
    setImporting(true);
    try {
      await axios.post(`${API}/api/patients/${patientId}/baby-menus/import`, { source_week_id: selectedWeek.id });
      toast.success('Ementa importada com sucesso');
      onOpenChange(false);
      setSelectedWeek(null);
      if (onImported) onImported();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao importar'); }
    finally { setImporting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl border-brand-border max-w-lg">
        <DialogHeader><DialogTitle className="font-heading text-brand-text">Importar ementa</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-brand-muted">Escolhe o paciente e a semana que queres copiar para este paciente.</p>
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Filtrar por nome..." className="pl-8 rounded-xl border-brand-border text-sm" data-testid="import-search" />
          </div>
          <div className="max-h-[300px] overflow-y-auto space-y-2">
            {loading ? <p className="text-xs text-brand-muted py-4 text-center">A carregar...</p> : sources.length === 0 ? (
              <p className="text-xs text-brand-placeholder py-4 text-center">Sem ementas disponíveis para importar.</p>
            ) : sources.map(s => (
              <div key={s.patient_id}>
                <p className="text-xs font-medium text-brand-text mb-1">{s.name}</p>
                <div className="space-y-1">
                  {s.weeks.map(w => (
                    <button key={w.id} onClick={() => setSelectedWeek(w)}
                      className={`w-full text-left px-3 py-2 rounded-xl text-sm border transition-all ${selectedWeek?.id === w.id ? 'border-brand-green bg-brand-green/5' : 'border-brand-border hover:border-brand-green/30'}`}
                      data-testid={`import-week-${w.id}`}>
                      <span className="font-medium">{w.label || `S${w.week_number}`}</span>
                      {w.focus && <span className="text-brand-muted ml-2">{w.focus}</span>}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <button onClick={handleImport} disabled={!selectedWeek || importing}
            className="w-full bg-brand-green text-white rounded-full py-3 font-medium disabled:opacity-50 flex items-center justify-center gap-2" data-testid="confirm-import-btn">
            {importing ? 'A importar...' : 'Importar semana'} <ArrowRight size={14} />
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function ImportMealPlanDialog({ open, onOpenChange, patientId, onImported }) {
  const [sources, setSources] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/meal-plans/available-for-import?search=${search}`);
        setSources(data.filter(s => s.patient_id !== patientId));
      } catch {} finally { setLoading(false); }
    })();
  }, [open, search, patientId]);

  const handleImport = async () => {
    if (!selectedPlan) return;
    setImporting(true);
    try {
      await axios.post(`${API}/api/patients/${patientId}/meal-plans/import`, { source_plan_id: selectedPlan.id });
      toast.success('Plano importado com sucesso');
      onOpenChange(false);
      setSelectedPlan(null);
      if (onImported) onImported();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao importar'); }
    finally { setImporting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl border-brand-border max-w-lg">
        <DialogHeader><DialogTitle className="font-heading text-brand-text">Importar plano alimentar</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-brand-muted">Escolhe o paciente e o plano que queres copiar. O plano será criado como rascunho.</p>
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Filtrar por nome..." className="pl-8 rounded-xl border-brand-border text-sm" data-testid="import-plan-search" />
          </div>
          <div className="max-h-[300px] overflow-y-auto space-y-2">
            {loading ? <p className="text-xs text-brand-muted py-4 text-center">A carregar...</p> : sources.length === 0 ? (
              <p className="text-xs text-brand-placeholder py-4 text-center">Sem planos disponíveis para importar.</p>
            ) : sources.map(s => (
              <div key={s.patient_id}>
                <p className="text-xs font-medium text-brand-text mb-1">{s.name}</p>
                <div className="space-y-1">
                  {s.plans.map(p => (
                    <button key={p.id} onClick={() => setSelectedPlan(p)}
                      className={`w-full text-left px-3 py-2 rounded-xl text-sm border transition-all ${selectedPlan?.id === p.id ? 'border-brand-green bg-brand-green/5' : 'border-brand-border hover:border-brand-green/30'}`}
                      data-testid={`import-plan-${p.id}`}>
                      <span className="font-medium">{p.name}</span>
                      <span className={`ml-2 text-[10px] px-2 py-0.5 rounded-full ${p.status === 'ativo' ? 'bg-brand-mint/20 text-brand-green' : 'bg-brand-bg text-brand-muted'}`}>{p.status}</span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <button onClick={handleImport} disabled={!selectedPlan || importing}
            className="w-full bg-brand-green text-white rounded-full py-3 font-medium disabled:opacity-50 flex items-center justify-center gap-2" data-testid="confirm-import-plan-btn">
            {importing ? 'A importar...' : 'Importar plano'} <ArrowRight size={14} />
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
